import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import ClientShell from "@/components/ClientShell";
import { ArrowLeft, Buildings, ArrowUpRight } from "@phosphor-icons/react";
import { formatDate } from "@/lib/format";
import StatusChip from "@/components/StatusChip";

/**
 * Projects view — groups orders by project_name or product_type.
 * If no project_name field, groups by product_type.
 */
export default function Projects() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/orders");
      setOrders(res.data);
    } catch (e) {
      console.error("[Projects] failed:", e);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const groups = useMemo(() => {
    const map = new Map();
    for (const o of orders) {
      const key = o.project_name || o.product_type || "Other";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(o);
    }
    return Array.from(map.entries());
  }, [orders]);

  return (
    <ClientShell>
      <Link to="/more" data-testid="projects-back" className="inline-flex items-center gap-2 mt-tiny text-zinc-500 hover:text-zinc-900 mb-6">
        <ArrowLeft size={14} /> Back to More
      </Link>

      <div className="mb-8 flex items-center gap-4">
        <div className="w-14 h-14 bg-[#0047AB]/10 text-[#0047AB] grid place-items-center">
          <Buildings size={28} weight="duotone" />
        </div>
        <div>
          <div className="mt-tiny text-[#FF5A1F]">Projects</div>
          <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight text-zinc-900 mt-1">
            Your projects
          </h1>
          <p className="mt-1 text-sm text-zinc-600">Orders grouped by project. Tap an order to see full details.</p>
        </div>
      </div>

      {loading ? (
        <div className="grid gap-3">{["s-1", "s-2"].map((k) => <div key={k} className="h-32 bg-white border border-zinc-200 animate-pulse" />)}</div>
      ) : groups.length === 0 ? (
        <div className="bg-white border border-zinc-200 p-14 text-center text-zinc-500">No projects yet.</div>
      ) : (
        <div className="grid gap-6" data-testid="projects-list">
          {groups.map(([name, items]) => (
            <section key={name} className="bg-white border border-zinc-200">
              <div className="flex items-center justify-between p-5 border-b border-zinc-200">
                <div>
                  <div className="mt-tiny text-zinc-400">Project</div>
                  <div className="font-display font-bold text-lg text-zinc-900 mt-0.5">{name}</div>
                </div>
                <div className="mt-tiny text-zinc-500">{items.length} order{items.length > 1 ? "s" : ""}</div>
              </div>
              <div className="divide-y divide-zinc-100">
                {items.map((o) => (
                  <Link
                    key={o.order_id}
                    to={`/orders/${o.order_id}`}
                    data-testid={`project-order-${o.order_number}`}
                    className="flex items-center justify-between gap-4 p-5 hover:bg-zinc-50 transition-colors group"
                  >
                    <div className="min-w-0">
                      <div className="mt-tiny text-zinc-400 font-mono-mt">{o.order_number}</div>
                      <div className="font-medium text-zinc-900 truncate group-hover:text-[#0047AB] transition-colors">{o.product_name}</div>
                      <div className="mt-tiny text-zinc-500 mt-1">Expected {formatDate(o.expected_delivery)}</div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <StatusChip status={o.current_status} />
                      <ArrowUpRight size={18} className="text-zinc-400 group-hover:text-[#FF5A1F] transition-colors" />
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </ClientShell>
  );
}
